import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import {
    mdiAccountSearch,
    mdiMapMarker,
    mdiMagnify,
    mdiClose,
    mdiCheckCircle,
    mdiCrosshairsGps,
} from '@mdi/js';
import { eventApi } from '../api/event';
import { useAuth } from '../contexts/AuthContext';
import StoreBanner from '../components/store-banner';
import { useMobileMenu } from '../hooks/useMobileMenu';
import danceLocaleIcon from '../assets/dance_locale_3-200h.png';
import SearchTypeSwitcher from '../components/SearchTypeSwitcher';
import InstructorCard from '../components/InstructorCard';
import { getDanceStyles, resolveToId } from '../config/danceStyles';
import './InstructorSearch.css';
import useRememberedSearchLocation, { readRememberedLocation } from "../hooks/useRememberedSearchLocation";

// Nearest-first page size; "Show more instructors" fetches the next page.
const PAGE_SIZE = 10;
const NEAR_ME_RADIUS_KM = 20;

/**
 * The browser's position, but only when location permission is ALREADY granted, so the
 * page never pops a permission prompt on load (the Near Me chip asks explicitly).
 * @returns {Promise<{latitude, longitude}|null>}
 */
const getGrantedPosition = async () => {
    try {
        if (!navigator.permissions || !navigator.geolocation) return null;
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state !== 'granted') return null;
        return await new Promise(resolve => {
            navigator.geolocation.getCurrentPosition(
                pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
                () => resolve(null),
                { maximumAge: 10 * 60 * 1000, timeout: 8000 }
            );
        });
    } catch {
        return null;
    }
};

const InstructorSearch = () => {
    const navigate = useNavigate();
    useMobileMenu();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
    // A ?location= in the URL always wins (a shared link means THAT place); only
    // fall back to what this browser last searched.
    const urlLocation = searchParams.get('location') || "";
    const remembered = urlLocation ? null : readRememberedLocation();
    const [searchLocation, setSearchLocation] = useState(urlLocation || remembered?.location || '');
    const [rawResults, setRawResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    // Filters
    const [selectedStyle, setSelectedStyle] = useState('');
    const [nearMe, setNearMe] = useState(false);

    // Remember the location for next visit. Persists appliedLocation — the value that
    // actually drives the results — so what is restored is what was really searched,
    // not half-typed text the visitor abandoned.
    const [appliedLocation, setAppliedLocation] = useState(urlLocation || remembered?.location || '');
    useRememberedSearchLocation({
        location: appliedLocation,
        urlHadLocation: Boolean(urlLocation),
    });
    const [userLocation, setUserLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // The current search's base params and how many nearest-first results it has
    // paged through, for "Show more instructors".
    const pagingRef = useRef({ params: null, offset: 0 });

    // A full page of nearest-first results (those carrying distanceKm) means there may
    // be another page. Mascots and visiting instructors come with the first page only
    // and sit outside the paged list, so they don't count toward the offset.
    const nearestCountOf = page =>
        page.filter(r => r.distanceKm !== undefined && !r.isMascot && !r.visiting).length;

    /**
     * Same ordering as the app's home list. With a point (the browser position, or a
     * typed location geocoded server-side): the mascot instructors first (no name
     * search), then everyone nearest-first, PAGE_SIZE at a time with distances. With
     * no point: the mascots, then the others in no order. Near Me narrows to 20 km.
     */
    const runSearch = async (query, location, { point = null, radiusKm = null } = {}) => {
        setLoading(true);
        setError(null);
        setHasSearched(true);
        setAppliedLocation(location);

        try {
            const params = { searchType: 'instructor', limit: String(PAGE_SIZE) };
            if (query) params.query = query;
            if (point) {
                params.latitude = String(point.latitude);
                params.longitude = String(point.longitude);
                if (radiusKm) params.radiusKm = String(radiusKm);
            } else if (location) {
                params.location = location;
                params.nearestToLocation = 'true';
            }
            // The demo mascots lead a browse, not a name search or a "near me" filter.
            if (!query && !radiusKm) params.pinMascots = 'true';

            // _t busts the browser cache: search results must always be fresh.
            const results = await eventApi.searchEvents({ ...params, offset: '0', _t: Date.now() });

            const nearestCount = nearestCountOf(results);
            pagingRef.current = { params, offset: nearestCount };
            setHasMore(nearestCount === PAGE_SIZE);
            setRawResults(results);
        } catch (err) {
            console.error('[InstructorSearch] Caught error:', err);
            setError('Failed to search instructors. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        const { params, offset } = pagingRef.current;
        if (!params || loadingMore) return;
        setLoadingMore(true);
        try {
            const page = await eventApi.searchEvents({ ...params, offset: String(offset), _t: Date.now() });
            const nearestCount = nearestCountOf(page);
            pagingRef.current = { params, offset: offset + nearestCount };
            setHasMore(nearestCount === PAGE_SIZE);
            setRawResults(prev => {
                const seen = new Set(prev.map(r => r.id));
                return [...prev, ...page.filter(r => !seen.has(r.id))];
            });
        } catch (err) {
            console.error('[InstructorSearch] Load more failed:', err);
            toast.error('Could not load more instructors. Please try again.');
        } finally {
            setLoadingMore(false);
        }
    };

    // A typed location wins; otherwise the browser position when already granted.
    const searchFromInputs = async (query, location) => {
        if (location) return runSearch(query, location);
        const point = userLocation || await getGrantedPosition();
        if (point) setUserLocation(point);
        return runSearch(query, '', { point });
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setNearMe(false);
        searchFromInputs(searchQuery, searchLocation);
    };

    // Auto-search on page load: the URL or remembered location, else the browser
    // position when already granted, else no point (mascots, then the others).
    useEffect(() => {
        searchFromInputs(searchQuery, searchLocation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The style filter narrows the loaded results live (no re-search needed).
    const instructors = useMemo(() => {
        if (!selectedStyle) return rawResults;
        // Compare by canonical id (resolves any legacy label/alias on either side).
        return rawResults.filter(instructor =>
            (instructor.danceStyles || []).some(s => resolveToId(s) === selectedStyle)
        );
    }, [rawResults, selectedStyle]);

    const clearSearch = () => {
        setSearchQuery('');
        setSearchLocation('');
        setSelectedStyle('');
        setNearMe(false);
        // Back to the landing browse (browser position when granted)
        searchFromInputs('', '');
    };

    // Near Me narrows the list to instructors within 20 km of the browser position,
    // searched server-side so it isn't limited to the page already loaded.
    const toggleNearMe = () => {
        if (nearMe) {
            setNearMe(false);
            searchFromInputs(searchQuery, searchLocation);
            return;
        }
        const searchNear = point => {
            setNearMe(true);
            runSearch(searchQuery, '', { point, radiusKm: NEAR_ME_RADIUS_KM });
        };
        if (userLocation) {
            searchNear(userLocation);
            return;
        }
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const point = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
                setUserLocation(point);
                setLocationLoading(false);
                searchNear(point);
            },
            () => {
                toast.error('Location access denied. Enable location permissions to use Near Me.');
                setLocationLoading(false);
            }
        );
    };

    return (
        <div className="instructor-search">
            {/* Header */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ textDecoration: 'none' }}>Home</a>
                        <a href="/instructors" className="home-text11" style={{ color: 'var(--dl-color-orange-default)', textDecoration: 'none' }}>Search</a>
                        {user && user.organisationId && <a href="/dashboard" className="home-text12" style={{ textDecoration: 'none' }}>Dashboard</a>}
                        {user && <a href="/my-hub" className="home-text12" style={{ textDecoration: 'none' }}>My Hub</a>}
                        <a href="/contact" className="home-text13" style={{ textDecoration: 'none' }}>Contact</a>
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14">Home</a>
                        <a href="/instructors" className="home-text15" style={{ color: 'var(--dl-color-orange-default)' }}>Search</a>
                        {user && user.organisationId && <a href="/dashboard" className="home-text16">Dashboard</a>}
                        {user && <a href="/my-hub" className="home-text16">My Hub</a>}
                        <a href="/contact" className="home-text17">Contact</a>
                    </div>
                </div>
            </div>

            {/* Hero Search Section */}
            <div className="search-hero">
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
                    {/* <button
                        onClick={() => navigate('/')}
                        style={{
                            position: 'absolute',
                            left: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'linear-gradient(135deg, var(--dl-color-purple-default) 0%, #6209c6 100%)',
                            border: '1px solid var(--dl-color-purple-default)',
                            color: 'white',
                            borderRadius: '18px',
                            padding: '8px 16px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                    >
                        ← Back to Home
                    </button> */}
                    <h1 style={{ margin: 0 }}>Find Dance Instructors</h1>
                </div>
                <p>Discover instructors in your area for private lessons, group classes, and online sessions</p>


                <form className="search-form" onSubmit={handleSearch}>
                    <div className="search-inputs">
                        <SearchTypeSwitcher active="instructors" />

                        <div className="search-input-wrapper">
                            <Icon path={mdiAccountSearch} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="Instructor name, dance style..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="search-input-wrapper">
                            <Icon path={mdiMapMarker} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="City or region..."
                                value={searchLocation}
                                onChange={(e) => setSearchLocation(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <button type="submit" className="btn-search" disabled={loading}>
                            <Icon path={mdiMagnify} size={1} />
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>

                    <div className="filter-chips">
                        <select
                            className="filter-select"
                            value={selectedStyle}
                            onChange={(e) => setSelectedStyle(e.target.value)}
                        >
                            <option value="">All styles</option>
                            {getDanceStyles().map(style => (
                                <option key={style.id} value={style.id}>{style.label}</option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className={`filter-chip ${nearMe ? 'active' : ''}`}
                            onClick={toggleNearMe}
                            disabled={locationLoading}
                        >
                            <Icon path={mdiCrosshairsGps} size={0.8} />
                            {locationLoading ? 'Locating...' : 'Near Me (20km)'}
                            {nearMe && <Icon path={mdiCheckCircle} size={0.7} />}
                        </button>

                        <button
                            type="button"
                            className="filter-chip"
                            onClick={() => toast.info('Instructor verification feature coming soon.')}
                        >
                            <Icon path={mdiCheckCircle} size={0.8} />
                            Verified Only
                        </button>

                        {(searchQuery || searchLocation || selectedStyle || nearMe) && (
                            <button type="button" onClick={clearSearch} className="btn-clear">
                                <Icon path={mdiClose} size={0.8} />
                                Clear
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Results Section */}
            <div className="results-container">
                {error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {loading && (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Searching for instructors...</p>
                    </div>
                )}

                {!loading && hasSearched && instructors.length === 0 && !error && (
                    nearMe ? (
                        <div className="empty-state">
                            <Icon path={mdiAccountSearch} size={3} color="var(--dl-color-lightplus)" />
                            <h3>No instructors within {NEAR_ME_RADIUS_KM} km</h3>
                            <p>See every instructor, nearest first.</p>
                            <button type="button" className="btn-search" onClick={toggleNearMe}>
                                Show all instructors
                            </button>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <Icon path={mdiAccountSearch} size={3} color="var(--dl-color-lightplus)" />
                            <h3>No instructors found</h3>
                            <p>Try adjusting your search terms or filters</p>
                        </div>
                    )
                )}

                {!loading && instructors.length > 0 && (
                    <>
                        <div className="instructors-grid">
                            {instructors.map((instructor) => (
                                <InstructorCard
                                    key={instructor.id}
                                    instructor={instructor}
                                    onClick={() => navigate(`/instructor/${instructor.id}`, {
                                        state: { from: 'search', query: searchQuery, searchLocation }
                                    })}
                                />
                        ))}
                        </div>
                        {hasMore && (
                            <div className="show-more-row">
                                <button type="button" className="btn-search" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Show more instructors'}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* CTA Section */}
                {!loading && hasSearched && (
                    <div className="cta-section">
                        <h2>Book Private Lessons On The Go</h2>
                        <p>Download the Dance Localé mobile app to book lessons, view availability, and connect with instructors</p>
                        <div className="cta-store-buttons">
                            <StoreBanner />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstructorSearch;
